import * as React from 'react';

import { isFunction, isNil } from 'lodash';
import { CustomEventHandler, SetState, AdjustCursor } from '../../commonTypes';
import { InputProps } from './types';
import {
  isSymbolAllowed, isSymbolForbidden, transformToCase, getSelection, getNewValueLength, getMaxPastedLength, getNewPastedValue,
} from './helpers';
import { stringToMaxLength } from '../../utils';

/**
 * Function creates change event handler
 * @param {InputProps} props - properties of component
 * @param {SetState<string>} setValue - Set state action
 * @param {AdjustCursor} adjustCursor - Function sets cursor to specified or previous position
 *
 * @returns {CustomEventHandler<React.ChangeEvent<HTMLInputElement>>} Change event handler
 */
export const createChangeHandler = (
  props: InputProps,
  setValue: SetState<string>,
  adjustCursor: AdjustCursor,
): CustomEventHandler<React.ChangeEvent<HTMLInputElement>> => (event): void => {
  const { value } = event.target;
  const {
    allowedSymbols, forbiddenSymbols, letterCase, maxLength,
  } = props;

  if (isSymbolForbidden(value, forbiddenSymbols)) return;

  if (!isSymbolAllowed(value, allowedSymbols)) return;

  // Next block prevents from replacing previous value if maxLength is exceeded
  // and sets cursor back to initial position
  if (!isNil(maxLength) && value.length > maxLength) {
    adjustCursor(event);
    return;
  }

  const maxLengthAdjustedValue = stringToMaxLength(value, maxLength);

  const newValue = letterCase ? transformToCase(value, letterCase) : maxLengthAdjustedValue;

  if (props.value === undefined) {
    setValue(newValue);
  }

  props.onChange?.({
    ...event,
    component: {
      name: props.name,
      value: newValue,
    },
  });
};

/**
 * Function creates clear event handler
 * @param {InputProps} props - Properties of component
 * @param {SetState<string>} setValue - Set state action
 * @param {React.MutableRefObject<HTMLInputElement | null>} inputRef - Input ref
 *
 * @returns {CustomEventHandler<React.MouseEvent<HTMLInputElement>>} Clear event handler
 */
export const createClearHandler = (
  props: InputProps,
  setValue: SetState<string>,
  inputRef: React.MutableRefObject<HTMLInputElement | null>,
): CustomEventHandler<React.MouseEvent<HTMLInputElement>> => (event): void => {
  event.preventDefault();

  if (inputRef.current) inputRef.current.focus();

  if (props.value === undefined) {
    setValue('');
  }

  props.onChange?.({
    ...event,
    component: {
      name: props.name,
      value: '',
    },
  });
};

/**
 * Function creates blur event handler
 * @param {InputProps} props - Properties of component
 * @param {SetState<boolean>} setFocused - Set state action
 * @param {function} validate - Validation function
 *
 * @returns {React.FocusEventHandler<HTMLInputElement>} Blur event handler
 */
export const createBlurHandler = (
  props: InputProps,
  setFocused: SetState<boolean>,
  validate: () => boolean,
): React.FocusEventHandler<HTMLInputElement> => (event): void => {
  setFocused(false);

  const newValid = validate();

  props.onBlur?.({
    ...event,
    component: {
      isValid: newValid,
      name: props.name,
      value: event.target.value,
    },
  });
};

/**
 * Function creates focus event handler
 * @param {InputProps} props - Properties of component
 * @param {boolean} isValid - Flag defines if input value is valid
 * @param {SetState<boolean>} setFocused - Set state action
 *
 * @returns {React.FocusEventHandler<HTMLInputElement>} Focus event handler
 */
export const createFocusHandler = (
  props: InputProps,
  isValid: boolean,
  setFocused: SetState<boolean>,
): React.FocusEventHandler<HTMLInputElement> => (event): void => {
  setFocused(true);

  props.onFocus?.({
    ...event,
    component: {
      isValid,
      name: props.name,
      value: event.target.value,
    },
  });
};

/**
 * Function creates key down event handler
 * @param {InputProps} props - Properties of component
 *
 * @returns {React.KeyboardEventHandler<HTMLInputElement>} Key down event handler
 */
export const createKeyDownHandler = (
  props: InputProps,
): React.KeyboardEventHandler<HTMLInputElement> => (event): void => {
  if (event.key === 'Enter') {
    props.onEnterPress?.({
      ...event,
      component: {
        name: props.name,
        value: event.currentTarget.value,
      },
    });
  }
};

/**
 * Function creates reset handler
 * @param {InputProps} props - Properties of component
 * @param {SetState<string>} setValue - Set state action
 *
 * @returns {void} Reset handler
 */
export const createResetHandler = (
  props: InputProps,
  setValue: SetState<string>,
) => (): void => {
  const newValue = props.defaultValue || '';

  setValue(newValue);

  props.onChange?.({
    component: {
      name: props.name,
      value: newValue,
    },
  });
};

/**
 * Function creates paste event handler which takes control if new value exceeds maxLength
 *
 * @param {InputProps} props - Properties of component
 * @param {string} value - Current value of input
 * @param {SetState<string>} setValue - Set state function for input value
 * @param {AdjustCursor} adjustCursor - Function sets cursor to specified or previous position if maxLength is exceeded
 *
 * @returns {CustomEventHandler<React.ClipboardEvent<HTMLInputElement>>} Paste event handler
 */
export const createPasteHandler = (
  props: InputProps,
  value: string,
  setValue: SetState<string>,
  adjustCursor: AdjustCursor,
): CustomEventHandler<React.ClipboardEvent<HTMLInputElement>> => (event): void => {
  const {
    maxLength, letterCase, isDisabled, onChange, name,
  } = props;
  const inputElement = event.target as HTMLInputElement;

  if (isDisabled) return;

  const { selectionStart, selectionEnd, selectedRange } = getSelection(inputElement);
  const pastedValue = event.clipboardData.getData('Text');
  const newValueLength = getNewValueLength(value, pastedValue, selectedRange);

  if (!isNil(maxLength) && newValueLength > maxLength) {
    const maxPastedLength = getMaxPastedLength(value, maxLength, selectedRange);

    const adjustedPastedValue = stringToMaxLength(pastedValue, maxPastedLength);

    const newValue = getNewPastedValue({
      adjustedPastedValue,
      maxLength,
      oldValue: value,
      selectedRange,
      selectionEnd,
      selectionStart,
    });

    if (newValue !== null) {
      event.preventDefault();

      const adjustedNewValue = letterCase ? transformToCase(newValue, letterCase) : newValue;

      if (props.value === undefined) {
        setValue(adjustedNewValue);
      }

      if (isFunction(onChange)) {
        const customEvent = {
          ...event,
          component: {
            name,
            value: adjustedNewValue,
          },
          target: event.target as HTMLInputElement,
        };

        onChange(customEvent);
      }

      if (selectionStart !== null) adjustCursor(event, selectionStart + maxPastedLength);
    } else {
      adjustCursor(event);
    }
  }
};
